// Fetch-based SSE client for the chat stream. Sends the JWT via the Authorization
// header (Bearer) so it works inside the cross-site preview iframe; also includes
// cookies.
//
// Cold-start resilience: the backend runs on a scale-to-zero platform, so the
// connection can fail ("Failed to fetch") or stall while the container wakes. We
// retry the initial connection on transient failures (network error / 502/503/504)
// with exponential backoff, refresh the token once on a 401, and run an inactivity
// watchdog so a dead stream surfaces an error instead of spinning "thinking…"
// forever.
import { getAccessToken, refreshSession, clearTokens } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_CONNECT_RETRIES = 3;
const CONNECT_RETRY_DELAYS = [500, 1000, 2000];
// If the stream sends no bytes for this long, treat the connection as dead.
const STALL_TIMEOUT_MS = 45000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isTransientStatus = (s) => s === 502 || s === 503 || s === 504;

export async function streamChat({ message, workspaceId, conversationId, sourceId, onEvent, signal }) {
  const body = JSON.stringify({
    message,
    workspace_id: workspaceId,
    conversation_id: conversationId || null,
    source_id: sourceId || null,
  });

  const doFetch = (innerSignal) => {
    const token = getAccessToken();
    return fetch(`${API}/chat/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      signal: innerSignal,
      body,
    });
  };

  // Establish the connection, retrying transient cold-start failures.
  let res = null;
  let attempt = 0;
  let refreshed = false;

  while (true) {
    const ac = new AbortController();
    const onAbort = () => ac.abort();
    if (signal) {
      if (signal.aborted) ac.abort();
      else signal.addEventListener("abort", onAbort, { once: true });
    }
    const connectTimer = setTimeout(() => ac.abort(), STALL_TIMEOUT_MS);
    try {
      res = await doFetch(ac.signal);
      clearTimeout(connectTimer);
      if (signal) signal.removeEventListener("abort", onAbort);

      // Token expired — refresh once and retry (doesn't consume a transient try).
      if (res.status === 401 && !refreshed) {
        refreshed = true;
        try {
          await refreshSession();
          continue;
        } catch {
          clearTokens();
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("sema:auth-expired"));
          }
          return;
        }
      }

      if (isTransientStatus(res.status) && attempt < MAX_CONNECT_RETRIES) {
        await sleep(CONNECT_RETRY_DELAYS[Math.min(attempt, CONNECT_RETRY_DELAYS.length - 1)]);
        attempt += 1;
        continue;
      }
      break;
    } catch (e) {
      clearTimeout(connectTimer);
      if (signal) signal.removeEventListener("abort", onAbort);
      // External abort (user navigated away / canceled) — don't retry.
      if (signal?.aborted) throw e;
      // Network error ("Failed to fetch") on cold boot — retry with backoff.
      if (attempt < MAX_CONNECT_RETRIES) {
        await sleep(CONNECT_RETRY_DELAYS[Math.min(attempt, CONNECT_RETRY_DELAYS.length - 1)]);
        attempt += 1;
        continue;
      }
      const err = new Error("We couldn't reach Sema — it may be waking up. Please re-run.");
      err.status = 0;
      throw err;
    }
  }

  if (!res.ok || !res.body) {
    let detail = null;
    try {
      detail = (await res.json()).detail;
    } catch {
      /* non-JSON error body */
    }
    const err = new Error(
      (detail && detail.message) || `Chat request failed (${res.status})`
    );
    err.status = res.status;
    err.detail = detail;
    throw err;
  }

  // Stream with an inactivity watchdog so a stalled connection never spins forever.
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    let timer;
    const timeoutP = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error("stream-stalled")), STALL_TIMEOUT_MS);
    });
    let result;
    try {
      result = await Promise.race([reader.read(), timeoutP]);
    } catch {
      try {
        await reader.cancel();
      } catch {
        /* ignore */
      }
      const err = new Error("The response stalled — Sema may be waking up. Please re-run.");
      err.status = 0;
      throw err;
    } finally {
      clearTimeout(timer);
    }

    const { done, value } = result;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop();
    for (const chunk of chunks) {
      let event = "message";
      let data = "";
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (data) {
        try {
          onEvent(event, JSON.parse(data));
        } catch {
          /* ignore malformed frame */
        }
      }
    }
  }
}
